import "./EventRegistrationForm.scss";

import { Alert, Button, Col, Container, Form, InputGroup, Modal, Row, Spinner, ToggleButton } from "react-bootstrap";
import Markdown from "markdown-to-jsx";
import { Voucher, VoucherType, validateVoucher } from "../../services/VoucherService";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCountryDisplayName, getCountryOptions } from "../../utils/countries";

import { ApplicationContext } from "../../contexts/ApplicationContext";
import { NumberFormatter } from "@internationalized/number";
import { PriceScaleType, Ticket } from "../../services/EventService";

const MIDDLEWARE_URL: string = import.meta.env.VITE_MIDDLEWARE_URL;
const PRIVACY_POLICY: string = import.meta.env.VITE_PRIVACY_POLICY;
const AGB_COMMUNITY: string = import.meta.env.VITE_AGB_COMMUNITY;
const AGB_CONFERENCE: string = import.meta.env.VITE_AGB_CONFERENCE;
const AGB_GENERAL: string = import.meta.env.VITE_AGB_GENERAL;
const QS_URL1_DE: string = import.meta.env.VITE_QS_URL1_DE;
const QS_URL1_EN: string = import.meta.env.VITE_QS_URL1_EN;
const QS_URL2_DE: string = import.meta.env.VITE_QS_URL2_DE;
const QS_URL2_EN: string = import.meta.env.VITE_QS_URL2_EN;
const QS_URL3_DE: string = import.meta.env.VITE_QS_URL3_DE;
const QS_URL3_EN: string = import.meta.env.VITE_QS_URL3_EN;
const QS_URL4_DE: string = import.meta.env.VITE_QS_URL4_DE;
const QS_URL4_EN: string = import.meta.env.VITE_QS_URL4_EN;
const TICKET_PRICE_SCALE_THRESHOLD: number = parseInt(import.meta.env.VITE_TICKET_PRICE_SCALE_THRESHOLD ?? '3', 10);
const TICKET_PRICE_SCALE_THRESHOLD_2: number = parseInt(import.meta.env.VITE_TICKET_PRICE_SCALE_THRESHOLD_2 ?? '5', 10);
const APP_VARIANT: string = import.meta.env.VITE_APP_VARIANT;
const MARKDOWN_EXTERNAL_LINK_OPTIONS = {
    overrides: {
        a: {
            props: {
                target: "_blank",
                rel: "noopener noreferrer",
            },
        },
    },
};

type Registration = {
    company: string,
    address: string,
    zip: string,
    city: string,
    country: string,
    contact_firstname: string,
    contact_name: string,
    contact_email: string,
    vat_number: string,
    internalReferenceNumber: string,
    participants: Participant[],
    ticket: string,
    newsletter: boolean,
    authorize_data_transfer: boolean,
    voucher?: Voucher,
    customerNote?: string,
    heard_from?: string,
    heard_from_other?: string;
};

type Participant = {
    title: string,
    salutation: string | undefined,
    firstname: string,
    name: string,
    email: string,
    phone: string,
    workshops: string[],

    jobtitle: string,
    company: string,
};

function EventRegistrationForm() {
    const { t, i18n } = useTranslation();
    const { eventItem, currentTicket, workshopTickets, setCurrentView } = useContext(ApplicationContext);

    const countryOptions = useMemo(
        () => getCountryOptions(i18n.language),
        [i18n.language],
    );

    const [useAlternateInvoiceMail, setUseAlternateInvoiceMail] = useState<boolean>(false);

    const [registration, setRegistration] = useState<Registration>({
        ticket: currentTicket.documentId,
        company: '',
        address: '',
        city: '',
        zip: '',
        country: '',
        contact_firstname: '',
        contact_name: '',
        contact_email: '',
        vat_number: '',
        internalReferenceNumber: '',
        authorize_data_transfer: false,
        newsletter: false,
        participants: [
            {
                title: '',
                salutation: undefined,
                firstname: '',
                name: '',
                email: '',
                phone: '',
                jobtitle: '',
                company: '',
                workshops: []
            }
        ],
        voucher: {},
        customerNote: '',
        heard_from: t('registration.none', { lng: 'de' }),
        heard_from_other: '',
    });

    const [showForm, setShowForm] = useState(true);
    const [showRegistrationState, setShowRegistrationState] = useState<number>(0);
    
    const [validated, setValidated] = useState(false);
    const [voucherValidated, setVoucherValidated] = useState<boolean>(false);
    const [voucherValidating, setVoucherValidating] = useState<boolean>(false);
    const [voucherCode, setVoucherCode] = useState<string>('');
    const [voucherError, setVoucherError] = useState<boolean>(false);
    
    const [showModal, setShowModal] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    const [errors, setErrors] = useState<any>({});

    const currencyFormatter = new NumberFormatter(i18n.language === 'de' ? 'de-DE' : 'en-US', {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const renderPrice = () => {
        return (currentTicket.freeofcharge === true) ?
            t('registration.free') :
            `${currencyFormatter.format(
                calculateCost(currentTicket, registration)
            )} (${t('registration.plusVAT')})`;
    }

    const calculateDiscount = (price: number, registration: Registration, isWorkshop: Boolean): number => {
        const voucher = registration.voucher;
        if (voucher && voucher.value) {
        switch (voucher.type) {
            case VoucherType.PERCENTAGE: {
                return price * (voucher.value ?? 0) / 100;
            }
            case VoucherType.SET_PRICE: {
                if (!isWorkshop) return Math.max(price - Math.min(price, voucher.value), 0);
                return 0;
            }
            case VoucherType.FIXED_AMOUNT: {
                return voucher.value;
            }
            default: return 0;
        }
    }
        return 0;
    }

    const calculateWorkshopDiscount = (priceTaxless: number, registration: Registration): number => {
        let eventDiscount = eventItem.workshopDiscount ?? 0;
        if (eventDiscount > priceTaxless) eventDiscount = priceTaxless;
        const totalDiscount = eventDiscount + calculateDiscount(priceTaxless - eventDiscount, registration, true);
        return totalDiscount;
    }

    const calculateTotalDiscount = (ticket: Ticket, registration: Registration): number => {
        const ticketList = createTicketList(ticket);
        const workshopList = createWorkshopList();
        const totalDiscount = 
            Array.from(ticketList.values()).reduce((sum, curr) => sum += calculateDiscount(curr.price_def, registration, false), 0)
			+ Array.from(workshopList.values()).reduce((sum, curr) => sum += calculateWorkshopDiscount(curr.price_def, registration), 0);

        return totalDiscount / 100;
    }

    const createTicketList = (ticket: Ticket): Array<{ price_def: number, price: string, title: string, participant: string, discount: number, discountValue: string, discountedPrice: string }> => {
        const ticketList = new Array<{ price_def: number, price: string, title: string, participant: string, discount: number, discountValue: string, discountedPrice: string }>;
        const participantCount = registration.participants.length;

        registration.participants.forEach((p: Participant, i: number) => {
            const fullName = `${p.firstname} ${p.name}`;
            const price = ticket.alt_price_positions?.includes(i + 1) ? ticket.alt_price ?? getBaseTicketPrice(ticket, participantCount) : getBaseTicketPrice(ticket, participantCount);
            const discount = registration.voucher?.value ?? 0;
            const discountvalue = calculateDiscount(price, registration, false);
            const discountedPrice = Math.max(0, price - discountvalue);

            ticketList.push({
                price_def: price,
                price: currencyFormatter.format(price / 100),
                title: ticket.title,
                participant: fullName,
                discount: discount,
                discountValue: currencyFormatter.format(discountvalue / 100),
                discountedPrice: currencyFormatter.format(discountedPrice / 100),
            })
        })

        return ticketList;
    }

    const createWorkshopList = (): Array<{ price_def: number, price: string; title: string; participant: string, discount: string, discountedPrice: string  }> => {
        const workshopList = new Array<{ price_def: number, price: string; title: string; participant: string, discount: string, discountedPrice: string  }>;

        const participantsWithWorkshops: Participant[] = registration.participants.filter(p => p.workshops.length > 0);
        let workshopDiscount = eventItem.workshopDiscount ?? 0;

        for (const participant of participantsWithWorkshops) {
            for (const workshopId of participant.workshops) {
                const workshop = workshopTickets.find((w: Ticket) => w.documentId === workshopId);

                if (workshopDiscount > workshop.price) workshopDiscount = workshop.price;
                workshopDiscount = workshopDiscount + calculateDiscount((workshop.price - workshopDiscount), registration, true);
                const fullName = `${participant.firstname} ${participant.name}`;
                workshopList.push({
                    price_def: workshop.price,
                    price: currencyFormatter.format(workshop.price / 100),
                    title: workshop.title,
                    participant: fullName,
                    discount: currencyFormatter.format(workshopDiscount / 100),
                    discountedPrice: currencyFormatter.format((workshop.price - workshopDiscount) / 100),
                });
            }
        }

        return workshopList;
    }

    const renderDetailedCost = () => {
        return (currentTicket.freeofcharge === true) ?
            <p><strong>{t('registration.total')}:</strong> {t('registration.free')}</p> :
            <table className="invoice-table">
                <thead>
                    <tr>
                        <td align="left">{t('registration.item')}</td>
                        <td align="right">{t('registration.price')}</td>
                        <td align="right">{t('registration.discount')}</td>
                        <td align="right">{t('registration.amount')}</td>
                    </tr>
                </thead>
                <tbody>
                    {createTicketList(currentTicket).map((ticket, ticketIndex) => 
                        <tr key={ticketIndex}>
                            <td>
                                {ticket.title}<br/>&emsp;{ticket.participant}
                            </td>
                            <td align="right">EUR&nbsp;{ticket.price}</td>
                            <td align="right">
                                {!!ticket.discountValue && (
                                    <>
                                        EUR&nbsp;{ticket.discountValue}
                                    </>
                                )}
                            </td>
                            <td align="right">
                                EUR&nbsp;{ticket.discountedPrice}
                            </td>
                        </tr>
                    )}
                    {createWorkshopList().map((workshop, workshopIndex) => 
                        <tr key={workshopIndex}>
                            <td>
                                {workshop.title}<br/>&emsp;{workshop.participant}
                            </td>
                            <td align="right">EUR&nbsp;{workshop.price}
                            </td>
                            <td align="right">
                                {!!workshop.discount && (
                                    <>
                                        EUR&nbsp;{workshop.discount}
                                    </>
                                )}
                            </td>
                            <td align="right">EUR&nbsp;{workshop.discountedPrice}
                            </td>
                        </tr>
                    )}
                </tbody>

                <tfoot>
                    <tr>
                        <td></td>
                        <td></td>
                        <th style={{ textAlign: 'right' }} align="right">{t('registration.totalDiscount')}</th>
                        <th style={{ textAlign: 'right' }} align="right">EUR&nbsp;{currencyFormatter.format(calculateTotalDiscount(currentTicket, registration))}
                        </th>
                    </tr>
                    <tr>
                        <td></td>
                        <td></td>
                        <th style={{ textAlign: 'right' }} align="right">{t('registration.tax')}&nbsp;19,00&nbsp;%</th>
                        <td style={{ textAlign: 'right' }} align="right">EUR&nbsp;{currencyFormatter.format(calculateCost(currentTicket, registration) * 0.19)}
                        </td>
                    </tr>
                    <tr>
                        <td></td>
                        <td></td>
                        <th style={{ textAlign: 'right' }}>{t('registration.total')}</th>
                        <th style={{ textAlign: 'right' }} align="right">EUR&nbsp;{currencyFormatter.format(calculateCost(currentTicket, registration) + calculateCost(currentTicket, registration) * 0.19)}</th>
                    </tr>
                </tfoot>
            </table>;
    }

    const isFree = () => {
        return (calculateCost(currentTicket, registration) === 0) || currentTicket.freeofcharge;
    }

    const isNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

    const calculateCost = (ticket: Ticket, registration: Registration): number => {
        const participantCount = registration.participants?.length ?? 0;
        const altPriceCount = getAltPriceCount(ticket.alt_price_positions ?? [], participantCount);

        const workshopCount = registration.participants.filter(p => p.workshops.length > 0).length ?? 0;

        const priceNormal = getBaseTicketPrice(ticket, participantCount);
        const priceAlt = ticket.alt_price ?? getBaseTicketPrice(ticket, participantCount);
        const workShopPrice = calculateWorkshopCost();

        const baseTicketCost = priceNormal * (participantCount - altPriceCount) + priceAlt * altPriceCount;
        const totalBefore = baseTicketCost + workShopPrice;

        const voucher = registration.voucher;

        if (!voucher || !isNumber(voucher.value)) return totalBefore / 100;

        switch (voucher.type) {
            case VoucherType.PERCENTAGE: {
                const percent = Math.min(100, Math.max(0, voucher.value));
                return (totalBefore - (totalBefore * percent) / 100) / 100;
            }

            case VoucherType.FIXED_AMOUNT: {
                return Math.max(0, (totalBefore - (voucher.value * (participantCount + workshopCount))) / 100);
            }

            case VoucherType.SET_PRICE: {
                const adjustedNormal = Math.min(priceNormal, voucher.value);
                const adjusterAlt = Math.min(priceAlt, voucher.value);

                const adjustedTicketCost = adjustedNormal * (participantCount - altPriceCount) + adjusterAlt * altPriceCount;
                return (adjustedTicketCost  + workShopPrice) / 100;
            }
            default:
                return totalBefore / 100;
        }
    }

    const getBaseTicketPrice = (ticket: Ticket, participantCount: number): number => {
        return getPriceScaleTicketPrice(ticket, participantCount) ?? ticket.price ?? 0;
    }

    const getPriceScaleTicketPrice = (ticket: Ticket, participantCount: number): number | undefined => {
        const ticketPrice = (ticket.price ?? 0);

        // Check second (higher) threshold first (e.g. 5+ participants)
        if (
            participantCount >= TICKET_PRICE_SCALE_THRESHOLD_2 &&
            ticket.price_scale_type_2 &&
            ticket.price_scale_value_2 != null
        ) {
            switch (ticket.price_scale_type_2) {
                case PriceScaleType.PERCENTAGE:
                    return Math.max((ticketPrice - (ticketPrice * ticket.price_scale_value_2 / 100)), 0);
                case PriceScaleType.SET_PRICE:
                    return ticket.price_scale_value_2;
                case PriceScaleType.FIXED_AMOUNT:
                    return Math.max((ticketPrice - ticket.price_scale_value_2), 0);
                default: break;
            }
        }

        // Check first threshold (e.g. 3+ participants)
        if (
            participantCount >= TICKET_PRICE_SCALE_THRESHOLD &&
            ticket.price_scale_type &&
            ticket.price_scale_value != null
        ) {
            switch (ticket.price_scale_type) {
                case PriceScaleType.PERCENTAGE:
                    return Math.max((ticketPrice - (ticketPrice * ticket.price_scale_value / 100)), 0);
                case PriceScaleType.SET_PRICE:
                    return ticket.price_scale_value;
                case PriceScaleType.FIXED_AMOUNT:
                    return Math.max((ticketPrice - ticket.price_scale_value), 0);
                default: break;
            }
        }

        return undefined;
    }

    const calculateWorkshopCost = (): number => {
        const participantsWithWorkshops: Participant[] = registration.participants.filter(p => p.workshops.length > 0);

        let workshopPrice = 0;
        let workshopDiscount = eventItem.workshopDiscount ?? 0;
        for (let participant of participantsWithWorkshops) {
            for (let workshopId of participant.workshops) {
                const workshop = workshopTickets.find((w: Ticket) => w.documentId === workshopId);
                workshopPrice += Math.max(0, workshop.price - workshopDiscount);
            }
        }
        if (workshopPrice < 0) workshopPrice = 0;
        return workshopPrice;
    }

    const getAltPriceCount = (altPricePositions: number[], participants: number): number => {
        let altPriceCount: number = 0;
        for (let position of altPricePositions) {
            if (position <= participants) altPriceCount++;
        }
        return altPriceCount;
    }

    const renderAGBUrl = () => {
        const type = eventItem.event_type;
        return type === 'COMMUNITY_DAYS' ? AGB_COMMUNITY : (type === 'CONFERENCE' ? AGB_CONFERENCE : AGB_GENERAL);
    }

    const getKombiTicketValidationError = (): string | null => {
        if (!currentTicket?.isKombiTicket) return null;

        if (!workshopTickets?.length) {
            return t('registration.workshopsNoWorkshopsAvailableError');
        }

        const missingCount = registration.participants.filter(p => !p.workshops?.length).length;
        if (missingCount > 0) {
            return t('registration.workshopsRequiredError', { count: missingCount });
        }

        return null;
    };

    const showConfirmationModal = (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        
        const form = event.currentTarget;

        const allEmails = registration.participants.map(p => p.email.trim()).filter(e => e !== "");
        const hasDuplicateEmails = allEmails.some((email, i) => allEmails.indexOf(email) !== i);

        if (currentTicket.min_participants && currentTicket.min_participants !== null && registration.participants.length < currentTicket.min_participants) {
            showDialog({
                type: 'info',
                title: t('common.hint'),
                content: t('registration.minParticipantsError', { count: currentTicket.min_participants, missing: currentTicket.min_participants - registration.participants.length })
            });
            return false;
        } else if (currentTicket.max_participants && currentTicket.max_participants !== null && registration.participants.length > currentTicket.max_participants) {
            showDialog({
                type: 'info',
                title: t('common.hint'),
                content: t('registration.maxParticipantsError', { count: currentTicket.max_participants, extra: registration.participants.length - currentTicket.max_participants })
            });
            return false;
        } else if (hasDuplicateEmails) {
            showDialog({
                type: 'info',
                title: t('common.hint'),
                content: t('registration.duplicateEmailsError')
            });
            return false;
        }

        const kombiError = getKombiTicketValidationError();
        if (kombiError) {
            showDialog({
                type: 'info',
                title: t('common.hint'),
                content: kombiError
            });
            setValidated(true);
            return false;
        }

        setValidated(true);

        if (form.checkValidity() !== false && !voucherError && !hasDuplicateEmails) {
            setShowRegistrationState(0);
            
            if (isFree()) {
                registration.company = '';
                registration.vat_number = '';
                registration.address = '';
                registration.city = '';
                registration.zip = '';
                registration.country = '';
            }
            setShowConfirmation(true);
        } else {
            setShowRegistrationState(2);
        }    
    }

    const handleSubmit = () => {

        setValidated(true);

        const kombiError = getKombiTicketValidationError();
        if (kombiError) {
            showDialog({
                type: 'info',
                title: t('common.hint'),
                content: kombiError
            });
            return;
        }

        setShowLoader(true);

        const registrationBody = {
            ...registration,
            participants: registration.participants.map((p) => ({
                ...p,
                newsletter: registration.newsletter,
                authorize_data_transfer: registration.authorize_data_transfer,
            })),
        };

        fetch(`${MIDDLEWARE_URL}/registrations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(registrationBody)
        }).then((res) => {
            setShowForm(false);
            setShowConfirmation(false);
            setShowLoader(false);

            if (res.status === 409 || res.status === 500) {
                throw new Error();
            }

            if (!currentTicket.confirmation_route) {
                const eventPathSegments = window.location.pathname.split('/');
                if (eventPathSegments[eventPathSegments.length - 1] === "") eventPathSegments.pop();
                eventPathSegments[eventPathSegments.length - 1] = 'bestaetigung';
                window.location.href = eventPathSegments.join('/');
            } else {
                window.location.href = currentTicket.confirmation_route;
            }
        }).catch((error) => {
            console.error('Registration response error: ', error);
            setShowLoader(false);
            setShowConfirmation(false);
            showDialog({
                type: 'error',
                title: t('common.error'),
                content: t('registration.generalError')
            });
        });
    };

    const validateVoucherCode = async () => {
        setVoucherValidating(true);

        const voucher = await validateVoucher(voucherCode, registration.ticket);

        if (!voucher.valid) {
            setVoucherError(true);
            handleVoucherChange(undefined)
        } else {
            setVoucherError(false);
            voucher.code = voucherCode;
            handleVoucherChange(voucher);
        }
        setVoucherValidated(true);
        setVoucherValidating(false);
    }

    const addParticipant = () => {
        if ((currentTicket.max_participants && registration.participants.length < currentTicket.max_participants) || !Number.isInteger(currentTicket.max_participants)) {
            setValidated(false);
            setRegistration({
                ...registration, participants: [...registration.participants, {
                    title: '',
                    salutation: undefined,
                    firstname: '',
                    name: '',
                    email: '',
                    phone: '',
                    workshops: [],
                    jobtitle: '',
                    company: '',
                }]
            });
        } else {
            showDialog({
                type: 'info',
                title: t('common.hint'),
                content: t('registration.maxParticipantsInfo', { count: currentTicket.max_participants })
            });
        }
    };

    const removeParticipant = (index: number) => {
        const updatedParticipants = registration.participants.filter((_, i) => i !== index);
        setRegistration({ ...registration, participants: updatedParticipants });
    };

    const handleParticipantChange = (index: number, field: keyof Participant, value: any) => {
        if (value instanceof String) value = value.trim();

        const updatedParticipants = registration.participants;
        updatedParticipants[index][field] = value;
        setRegistration({ ...registration, participants: updatedParticipants });

        if (field === "email") {
            const allEmails = registration.participants.map(p => p.email).filter(e => e !== "");
            const duplicateEmails = allEmails.filter((e, i) => allEmails.indexOf(e) !== i);
            
            setErrors((prev: any) => ({
                ...prev,
                [index]: {
                    ...prev[index],
                    email: duplicateEmails.includes(value)
                }
            }));
        }
    };

    const handleRegistrationChange = (field: string, value: string | boolean) => {
        setRegistration({ ...registration, [field]: value });
    };

    const handleWorkshopSelectionChanged = (workshop: string, index: number) => {
        const participant: Participant | undefined = registration.participants[index];
        if (participant) {
            if (participant.workshops.includes(workshop)) {
                participant.workshops = participant.workshops.filter(w => w !== workshop);
            } else {
                participant.workshops.push(workshop);
            }
            handleParticipantChange(index, 'workshops', participant.workshops);
        } 
    }

    const handleVoucherChange = (voucher: Voucher | undefined): void => {
        setRegistration({ ...registration, voucher: voucher });
    }

    const handleVoucherCodeChange = (code: string) => {
        if (code.toLocaleUpperCase().trim() !== voucherCode) {
            setVoucherCode(code);
            setVoucherValidated(false);
            setVoucherError(false);
            const emptyVoucher = {
                code: '',
                value: 0,
            }
            handleVoucherChange(emptyVoucher);
        }
    }

    type ModalProps = {
        type: 'info' | 'error';
        title: string;
        content: string;
    }
    const [modalContent, setModalContent] = useState<ModalProps | undefined>();
    const showDialog = (props: ModalProps) => {
        setModalContent(props);
        setShowModal(true);
    };

    const showTicketsOverview = () => {
        setCurrentView('eventViewTickets');
    }

    return (
        <>
            {currentTicket &&
                <>
                    <Modal show={showLoader} backdrop="static" centered>
                        <Modal.Body className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
                            <Spinner animation="border" role="status" />
                            <span className="ms-3">{t('common.loading')}</span>
                        </Modal.Body>
                    </Modal>
                    <Modal size="lg" show={showModal} backdrop={true} centered scrollable onHide={() => setShowModal(false)}>
                        <Modal.Header className="py-4 px-5" closeButton onHide={() => setShowModal(false)}>
                            <Modal.Title className="text-primary">{modalContent?.title}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="py-3 px-5">
                            <div className="d-flex flex-column gap-1">
                                {modalContent?.content.split('\n').map((p, pi) =>
                                    p.trim() ? <p className="w-75 p-0 m-0 fs-6" key={pi}>{p}</p> : null
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary rounded-pill'}`} onClick={() => setShowModal(false)}>{t('common.close')}</Button>
                        </Modal.Footer>
                    </Modal>
                    <Modal size="lg" show={showConfirmation} backdrop={true} centered scrollable onHide={() => setShowConfirmation(false)}>
                        <Modal.Header className="py-4 px-5" closeButton onHide={() => setShowConfirmation(false)}>
                                <Modal.Title className="text-primary">{t('registration.summary')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="py-3 px-5">
                            <div>
                                <div className="mb-3">
                                    <h4>{t('registration.participants')}</h4>
                                    {registration && registration.participants.map((p, pIndex) => 
                                        <div key={pIndex} className="mb-2 border border-1 rounded px-2 py-1">
                                            {
                                                p.title &&
                                                <p><strong>{t('registration.jobTitle')}:</strong> {p.title}</p>
                                            }
                                            <p><strong>{t('registration.lastName')}:</strong> {p.firstname} {p.name}</p>
                                            <p><strong>{t('registration.email')}:</strong> {p.email}</p>
                                            {
                                                p.company &&
                                                <p><strong>{t('registration.company')}:</strong> {p.company}</p>

                                            }
                                            <p><strong>{t('registration.jobTitle')}:</strong> {p.jobtitle}</p>
                                            {
                                                p.workshops.length > 0 &&
                                                <p><strong>{t('registration.workshops')}:</strong> { p.workshops.map(w => workshopTickets.find((t: Ticket) => t.documentId === w).title).join(', ')} </p>
                                            }
                                        </div>
                                    )}
                                </div>
                                {
                                    registration.contact_email &&
                                    <div className="mb-3">
                                        <h4>{t('registration.contactPerson')}</h4>
                                        <div className="border border-2 rounded px-2 py-1">
                                            <p><strong>{t('registration.lastName')}:</strong> {registration.contact_firstname} {registration.contact_name}</p>
                                            <p><strong>{t('registration.email')}:</strong> {registration.contact_email}</p>
                                        </div>
                                    </div>
                                }
                                {registration && (registration.company || registration.address) &&
                                    <div className="mb-3">
                                        <h4>{t('registration.billingAddress')}</h4>
                                        <div className="border border-2 rounded px-2 py-1">
                                            <p><strong>{t('registration.company')}:</strong> {registration.company}</p>
                                            <p><strong>{t('registration.address')}:</strong> {registration.address}, {registration.zip} {registration.city} ({getCountryDisplayName(registration.country, i18n.language)})</p>
                                            <p><strong>{t('registration.vat')}:</strong> {registration.vat_number}</p>
                                            <p><strong>{t('registration.refNumber')}:</strong> {registration.internalReferenceNumber}</p>
                                        </div>
                                    </div>
                                }
                                <div className="mb-3">
                                    <h4>{t('registration.costs')}</h4>
                                    <div className="border border-2 rounded px-2 py-1">
                                        {renderDetailedCost()}
                                    </div>
                                </div>
                                {
                                    !isFree() &&
                                        <p>
                                            {eventItem.optInRequired ? t('registration.paymentHintDOI') : t('registration.paymentHint')}
                                        </p>
                                }
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant={`${APP_VARIANT === 'QS' ? 'primary rounded-0' : 'secondary rounded-pill'}`} onClick={() => {setShowConfirmation(false); setValidated(false);}}>{t('common.edit')}</Button>
                            <Button variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary rounded-pill'}`} onClick={() => handleSubmit()}>{t('common.submit')}</Button>
                        </Modal.Footer>
                    </Modal>
                    <Container style={{ maxWidth: '768px' }}>
                        <Row className="mb-5 event__registration-back-to-tickets">
                            <Col>
                                <Button onClick={() => showTicketsOverview() } variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary rounded-pill'}`}>{t('registration.backToTickets')}</Button>
                            </Col>
                        </Row>
                        <Row className="mb-5 event__registration-ticket-header">
                            <Col sm={8} className="event__registration-ticket-header-title-description">
                                <h2>{eventItem.title} <br /> {currentTicket.title}</h2>
                                {currentTicket.description &&
                                
                                    <p dangerouslySetInnerHTML={{ __html: currentTicket.description ? currentTicket.description.replace(/\n/g, '<br />') : null}}></p>
                                }
                            </Col>
                            <Col sm={4} className="d-flex justify-content-md-end fs-3 event__registration-ticket-header-price">{renderPrice()}</Col>
                        </Row>
                        <Row className="mb-5" hidden={showRegistrationState !== 2}>
                            <Col>
                                <Alert variant="warning">
                                    {t('registration.checkInput')}
                                </Alert>
                            </Col>
                        </Row>
                        {currentTicket.isKombiTicket && (!workshopTickets || workshopTickets.length === 0) &&
                            <Row className="mb-5">
                                <Col>
                                    <Alert variant="danger">
                                        {t('registration.workshopsNoWorkshopsAvailableError')}
                                    </Alert>
                                </Col>
                            </Row>
                        }
                        <Row>
                            <Col>
                                <Form className="event__registration-form" noValidate validated={validated} onSubmit={showConfirmationModal} hidden={!showForm}>
                                    {registration.participants.map((contactPerson, index) => (
                                        <Form.Group key={index} className="mb-5 event__registration-formgroup">
                                            <h2 className="text-primary mb-4">{t('registration.participant')} #{index + 1}</h2>
                                            <Form.Group className="mb-4 event__registration-formgroup">
                                                <Row>
                                                    <Col>
                                                        <Form.Label htmlFor={`participant-${index}-title`}>{t('registration.honorificTitle')}</Form.Label>
                                                        <Form.Control id={`participant-${index}-title`} name={`participant-${index}-honorific-prefix`} autoComplete={`section-participant-${index} honorific-prefix`} value={contactPerson.title} onChange={(e) => handleParticipantChange(index, 'title', e.target.value)} type="text" />
                                                        <Form.Control.Feedback type="invalid">
                                                            {t('registration.fillOut')}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                    { APP_VARIANT === "QS" &&
                                                    <Col>
                                                        <Form.Label htmlFor={`participant-${index}-salutation`}>{t('registration.salutation')} {APP_VARIANT === 'QS' && '*'}</Form.Label>
                                                        <Form.Select id={`participant-${index}-salutation`} name={`participant-${index}-salutation`} autoComplete="off" value={contactPerson.salutation ?? ''} onChange={(e) => handleParticipantChange(index, 'salutation', e.target.value || undefined)} required={APP_VARIANT === 'QS'}>
                                                            <option value="" disabled hidden>{t('registration.select')}</option>
                                                            <option key={"Herr"} value={"Herr"}>{t('registration.mr')}</option>
                                                            <option key={"Frau"} value={"Frau"}>{t('registration.mrs')}</option>
                                                            <option key={"Divers"} value={"Divers"}>{t('registration.other')}</option>
                                                            <option key={"KeineAngabe"} value={"Keine Angabe"}>{t('registration.none')}</option>
                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {t('registration.fillOut')}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                    }
                                                </Row>
                                            </Form.Group>
                                            <Form.Group className="mb-4 event__registration-formgroup">
                                                <Row>
                                                    <Col>
                                                        <Form.Label htmlFor={`participant-${index}-firstname`}>{t('registration.firstName')} *</Form.Label>
                                                        <Form.Control id={`participant-${index}-firstname`} name={`participant-${index}-firstname`} autoComplete={`section-participant-${index} given-name`} value={contactPerson.firstname} onChange={(e) => handleParticipantChange(index, 'firstname', e.target.value)} required type="text" />
                                                        <Form.Control.Feedback type="invalid">
                                                            {t('registration.fillOut')}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                    <Col>
                                                        <Form.Label htmlFor={`participant-${index}-lastname`}>{t('registration.lastName')} *</Form.Label>
                                                        <Form.Control id={`participant-${index}-lastname`} name={`participant-${index}-lastname`} autoComplete={`section-participant-${index} family-name`} value={contactPerson.name} onChange={(e) => handleParticipantChange(index, 'name', e.target.value)} required type="text" />
                                                        <Form.Control.Feedback type="invalid">
                                                            {t('registration.fillOut')}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Row>
                                            </Form.Group>
                                            <Form.Group className="mb-4 event__registration-formgroup">
                                                <Form.Label htmlFor={`participant-${index}-company`}>{t('registration.company')} *</Form.Label>
                                                <Form.Control id={`participant-${index}-company`} name={`participant-${index}-organization`} autoComplete={`section-participant-${index} organization`} required value={contactPerson.company} onChange={(e) => handleParticipantChange(index, 'company', e.target.value)} type="text" />
                                                <Form.Control.Feedback type="invalid">
                                                    {t('registration.fillOut')}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group className="mb-4 event__registration-formgroup">
                                                <Form.Label htmlFor={`participant-${index}-jobtitle`}>{t('registration.jobTitle')} *</Form.Label>
                                                <Form.Control id={`participant-${index}-jobtitle`} name={`participant-${index}-organization-title`} autoComplete={`section-participant-${index} organization-title`} required value={contactPerson.jobtitle} onChange={(e) => handleParticipantChange(index, 'jobtitle', e.target.value)} type="text" />
                                                <Form.Control.Feedback type="invalid">
                                                    {t('registration.fillOut')}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group className="mb-4 event__registration-formgroup">
                                                <Row>
                                                    <Col>
                                                        <Form.Label htmlFor={`participant-${index}-email`}>{t('registration.email')} *</Form.Label>
                                                        <Form.Control id={`participant-${index}-email`} name={`participant-${index}-email`} autoComplete={`section-participant-${index} email`} value={contactPerson.email} onChange={(e) => handleParticipantChange(index, 'email', e.target.value)} required type="email" isInvalid={errors[index]?.email} pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$" />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors[index]?.email
                                                                ? t('registration.emailDuplicateError')
                                                                : t('registration.emailInvalidError')}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                    <Col>
                                                        <Form.Label htmlFor={`participant-${index}-phone`}>{t('registration.phone')} *</Form.Label>
                                                        <Form.Control id={`participant-${index}-phone`} name={`participant-${index}-phone`} autoComplete={`section-participant-${index} tel`} value={contactPerson.phone} onChange={(e) => handleParticipantChange(index, 'phone', e.target.value)} required />
                                                        <Form.Control.Feedback type="invalid">
                                                            {t('registration.fillOut')}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Row>
                                            </Form.Group>
                                            {
                                                workshopTickets && workshopTickets.length > 0 && 
                                                <div>
                                                    <h4 className="text-primary mt-2">
                                                        {currentTicket.isKombiTicket
                                                            ? t('registration.workshopsRequiredHeader')
                                                            : t('registration.workshopsHeader')}
                                                    </h4>
                                                    <small>
                                                        {currentTicket.isKombiTicket
                                                            ? t('registration.workshopsRequiredDescription')
                                                            : t('registration.workshopsDescription')}
                                                        <br></br>
                                                    </small>
                                                    {
                                                        workshopTickets.map((w: Ticket, i: number) => 
                                                            <div key={`${w.documentId!}${index}${i}`} className="mt-1">
                                                                        <ToggleButton 
                                                                            type="checkbox"
                                                                            checked={contactPerson.workshops.includes(w.documentId!)}
                                                                            value={`${w.documentId!}${index}${i}`}
                                                                            className={`border border-1 ${APP_VARIANT === 'QS' ? 'rounded-0' : 'border-rounded-3'} border-secondary toggle-btn`}
                                                                            style={{ width: '100%', backgroundColor: 'white', color: 'black' }}
                                                                            id={`${w.documentId!}${index}${i}`}
                                                                            onChange={() => handleWorkshopSelectionChanged(w.documentId!, index)}>
                                                                            <Row>
                                                                                <Col xs="auto">
                                                                                    <Form.Check
                                                                                        type="checkbox"
                                                                                        checked={contactPerson.workshops.includes(w.documentId!)}
                                                                                        onChange={() => handleWorkshopSelectionChanged(w.documentId!, index)}>
                                                                                    </Form.Check>
                                                                                </Col>
                                                                                <Col className="text-start">
                                                                                    <span>{w.title}</span>
                                                                                </Col>
                                                                                <Col className="text-end">
                                                                                    {
                                                                                        eventItem.workshopDiscount &&
                                                                                        <div>
                                                                                            <span>{currencyFormatter.format((eventItem.workshopDiscount && (w.price! - eventItem.workshopDiscount > 0) ? w.price! - eventItem.workshopDiscount : w.price!) / 100)}€ </span>
                                                                                            <span className="text-decoration-line-through">{currencyFormatter.format(w.price! / 100)}€</span>
                                                                                        </div>
                                                                                    }
                                                                                    {
                                                                                        !eventItem.workshopDiscount &&
                                                                                            <span>{currencyFormatter.format(w.price! / 100)}</span>
                                                                                    }
                                                                                </Col>
                                                                            </Row>
                                                                        </ToggleButton>
                                                            </div>
                                                        )
                                                    }
                                                    {validated && currentTicket.isKombiTicket && !contactPerson.workshops.length &&
                                                        <div className="text-danger mt-1">
                                                            {t('registration.workshopsRequiredParticipantError')}
                                                        </div>
                                                    }
                                                </div>
                                            }
                                            {index > 0 &&
                                                <Button variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary rounded-pill'} event__registration-removepersonbutton mt-4`} onClick={() => removeParticipant(index)}>{t('registration.removeParticipant')}</Button>
                                            }
                                        </Form.Group>
                                    ))}
                                    <Button className="mb-5" variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary rounded-pill'} event__registration-addpersonbutton`} onClick={addParticipant}>{t('registration.addParticipants')}</Button>
                                    
                                    {/* Kontaktperson */}
                                    <Form.Group className="mb-5 event__registration-formgroup" controlId="formAlternateInvoiceMail">
                                        <Form.Check type="checkbox" checked={useAlternateInvoiceMail} onChange={(e) => setUseAlternateInvoiceMail(e.target.checked)} label={t('registration.alternateEmail')} />
                                            <small>{t('registration.alternateEmailDescription')}</small>
                                        {
                                            useAlternateInvoiceMail &&
                                            <div>

                                                <Form.Group className="mb-4 mt-3">
                                                    <Row>
                                                        <Col>
                                                            <Form.Label htmlFor="contact-firstname">{t('registration.firstNameRequired')} *</Form.Label>
                                                            <Form.Control id="contact-firstname" name="contact-firstname" autoComplete="section-invoice given-name" value={registration.contact_firstname} onChange={(e) => handleRegistrationChange('contact_firstname', e.target.value)} required type="text" />
                                                            <Form.Control.Feedback type="invalid">
                                                                {t('registration.fillOut')}
                                                            </Form.Control.Feedback>
                                                        </Col>
                                                        <Col>
                                                            <Form.Label htmlFor="contact-lastname">{t('registration.lastNameRequired')} *</Form.Label>
                                                            <Form.Control id="contact-lastname" name="contact-lastname" autoComplete="section-invoice family-name" value={registration.contact_name} onChange={(e) => handleRegistrationChange('contact_name', e.target.value)} required type="text" />
                                                            <Form.Control.Feedback type="invalid">
                                                                {t('registration.fillOut')}
                                                            </Form.Control.Feedback>
                                                        </Col>
                                                    </Row>
                                                </Form.Group>
                                                <Form.Group className="mb-4">
                                                    <Form.Label htmlFor="contact-email">{t('registration.email')} *</Form.Label>
                                                    <Form.Control id="contact-email" name="contact-email" autoComplete="section-invoice email" value={registration.contact_email} onChange={(e) => handleRegistrationChange('contact_email', e.target.value)} required type="email" pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$" />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t('registration.emailInvalidError')}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </div>
                                        }
                                        </Form.Group>
                                    {/* Rechnungsadresse */}
                                    {!isFree() && 
                                    <Form.Group className="mb-5 event__registration-formgroup" controlId="formBillingAddress">
                                        <h2 className="text-primary mb-4">{t('registration.billingAddress')}</h2>
                                        <Form.Group className="mb-4">
                                            <Form.Label htmlFor="billing-organization">{t('registration.company')}</Form.Label>
                                            <Form.Control id="billing-organization" name="billing-organization" autoComplete="section-invoice organization" value={registration.company} onChange={(e) => handleRegistrationChange('company', e.target.value)} type="text" />
                                            <Form.Control.Feedback type="invalid">
                                                {t('registration.fillOut')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Form.Label htmlFor="billing-vat-number">{t('registration.vat')}</Form.Label>
                                            <Form.Control id="billing-vat-number" name="billing-vat-number" autoComplete="off" value={registration.vat_number} onChange={(e) => handleRegistrationChange('vat_number', e.target.value)} type="text" aria-describedby="vatNumberInfoBlock"/>
                                            <Form.Text id="vatNumberInfoBlock" muted>
                                                {t('registration.vatHint')}
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {t('registration.fillOut')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Form.Label htmlFor="billing-reference-number">{t('registration.refNumber')} <small>({t('registration.summary').toLowerCase()})</small></Form.Label>
                                            <Form.Control id="billing-reference-number" name="billing-reference-number" autoComplete="off" value={registration.internalReferenceNumber} onChange={(e) => handleRegistrationChange('internalReferenceNumber', e.target.value)} type="text" />
                                            <Form.Control.Feedback type="invalid">
                                                {t('registration.fillOut')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Form.Label htmlFor="billing-street-address">{t('registration.street')} *</Form.Label>
                                            <Form.Control id="billing-street-address" name="billing-street-address" autoComplete="section-invoice street-address" required type="text" value={registration.address} onChange={(e) => handleRegistrationChange('address', e.target.value)} />
                                            <Form.Control.Feedback type="invalid">
                                                {t('registration.fillOut')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Row>
                                                <Col>
                                                    <Form.Label htmlFor="billing-postal-code">{t('registration.zip')} *</Form.Label>
                                                    <Form.Control id="billing-postal-code" name="billing-postal-code" autoComplete="section-invoice postal-code" required type="text" value={registration.zip} onChange={(e) => handleRegistrationChange('zip', e.target.value)} />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t('registration.fillOut')}
                                                    </Form.Control.Feedback>
                                                </Col>
                                                <Col>
                                                    <Form.Label htmlFor="billing-city">{t('registration.city')} *</Form.Label>
                                                    <Form.Control id="billing-city" name="billing-city" autoComplete="section-invoice address-level2" required type="text" value={registration.city} onChange={(e) => handleRegistrationChange('city', e.target.value)} />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t('registration.fillOut')}
                                                    </Form.Control.Feedback>
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        <Form.Group className="mb-4" controlId="billing-country">
                                            <Form.Label>{t('registration.country')} *</Form.Label>
                                            <Form.Select
                                                name="billing-country"
                                                autoComplete="section-invoice country"
                                                required
                                                value={registration.country}
                                                onChange={(e) => handleRegistrationChange('country', e.target.value)}
                                            >
                                                <option value="">{t('registration.countryPlaceholder')}</option>
                                                {countryOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {t('registration.fillOut')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-4" controlId="billing-customer-note">
                                            <Form.Label>{t('registration.notes')}</Form.Label>
                                            <Form.Control autoComplete="off" type="text" as="textarea" value={registration.customerNote} onChange={(e) => handleRegistrationChange('customerNote', e.target.value)} />
                                            <Form.Control.Feedback type="invalid">
                                                {t('registration.fillOut')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Form.Group>
                                    }
                                    {/* Rabatt Code */}
                                    <Form.Group className="mb-4 event__registration-formgroup" controlId="formVoucherCode">
                                        <Form.Label>{t('registration.voucherCode')}</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control autoComplete="off" value={voucherCode} onChange={(e) => handleVoucherCodeChange(e.target.value)} type="text" disabled={voucherValidating} isInvalid={voucherError} />
                                            <Button variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary'}`} id="button-coupon" onClick={validateVoucherCode} disabled={voucherValidating}>
                                                {voucherValidating ? <Spinner animation="border" size="sm" /> : t('registration.checkVoucher')}
                                            </Button>
                                        </InputGroup>
                                        {voucherValidated && (
                                            voucherError ? (
                                                <Form.Text className="text-danger">{t('registration.voucherInvalid')}</Form.Text>
                                            )
                                                : (
                                                    <Form.Text className="text-success">{t('registration.voucherApplied')}</Form.Text>
                                                )
                                        )}
                                    </Form.Group>

                                    {/* Optional Questionnaire */}
                                    {APP_VARIANT === 'QS' && (
                                        <Form.Group className="mb-4 event__registration-formgroup" controlId="formHeardFrom">
                                            <Form.Label>{t('registration.heardFromLabel')}</Form.Label>
                                            <Form.Select
                                                autoComplete="off"
                                                value={registration.heard_from}
                                                onChange={(e) => handleRegistrationChange('heard_from', e.target.value)}
                                            >
                                                <option value={t('registration.none', { lng: 'de' })}>{t('registration.none')}</option>
                                                <option value={t('registration.heardFromOptions.webpage', { lng: 'de' })}>{t('registration.heardFromOptions.webpage')}</option>
                                                <option value={t('registration.heardFromOptions.newsletter', { lng: 'de' })}>{t('registration.heardFromOptions.newsletter')}</option>
                                                <option value={t('registration.heardFromOptions.facebook', { lng: 'de' })}>{t('registration.heardFromOptions.facebook')}</option>
                                                <option value={t('registration.heardFromOptions.xing', { lng: 'de' })}>{t('registration.heardFromOptions.xing')}</option>
                                                <option value={t('registration.heardFromOptions.linkedin', { lng: 'de' })}>{t('registration.heardFromOptions.linkedin')}</option>
                                                <option value={t('registration.heardFromOptions.searchEngine', { lng: 'de' })}>{t('registration.heardFromOptions.searchEngine')}</option>
                                                <option value={t('registration.heardFromOptions.recommendation', { lng: 'de' })}>{t('registration.heardFromOptions.recommendation')}</option>
                                                <option value={t('registration.heardFromOptions.press', { lng: 'de' })}>{t('registration.heardFromOptions.press')}</option>
                                                <option value={t('registration.heardFromOptions.ad', { lng: 'de' })}>{t('registration.heardFromOptions.ad')}</option>
                                                <option value={t('registration.heardFromOptions.gtb', { lng: 'de' })}>{t('registration.heardFromOptions.gtb')}</option>
                                                <option value={t('registration.heardFromOptions.istqb', { lng: 'de' })}>{t('registration.heardFromOptions.istqb')}</option>
                                                <option value={t('registration.heardFromOptions.other', { lng: 'de' })}>{t('registration.heardFromOptions.other')}</option>
                                            </Form.Select>
                                            {registration.heard_from === t('registration.heardFromOptions.other', { lng: 'de' }) && (
                                                <div className="mt-3">
                                                    <Form.Label htmlFor="formHeardFromOther">{t('registration.heardFromOtherLabel')}</Form.Label>
                                                    <Form.Control
                                                        id="formHeardFromOther"
                                                        autoComplete="off"
                                                        type="text"
                                                        value={registration.heard_from_other}
                                                        onChange={(e) => handleRegistrationChange('heard_from_other', e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        </Form.Group>
                                    )}

                                    {/* Newsletter */}
                                    {APP_VARIANT === 'QS' && (
                                        <div className="mb-5 event__registration-notes">
                                            <h2 className="text-secondary mb-3">{t('registration.noteTitle')}</h2>
                                            <ul className="list-unstyled">
                                                <li className="mb-3 d-flex align-items-baseline">
                                                    <span className="text-secondary me-2">•</span>
                                                    <span>{t('registration.note1')}</span>
                                                </li>
                                                <li className="mb-3 d-flex align-items-baseline">
                                                    <span className="text-secondary me-2">•</span>
                                                    <span><Markdown options={MARKDOWN_EXTERNAL_LINK_OPTIONS}>{t('registration.note2', { url: PRIVACY_POLICY })}</Markdown></span>
                                                </li>
                                            </ul>
                                            <h2 className="text-secondary mb-3 mt-4">{t('registration.privacyAndTerms')}</h2>
                                        </div>
                                    )}
                                    <Form.Group className="mb-4 event__registration-formgroup" controlId="formNewsletter">
                                        <Form.Check 
                                            type="checkbox" 
                                            checked={registration.newsletter} 
                                            onChange={(e) => handleRegistrationChange('newsletter', e.target.checked)} 
                                            label={
                                                APP_VARIANT === 'QS' ? (
                                                    <span className="d-flex align-items-baseline">
                                                        <Markdown options={MARKDOWN_EXTERNAL_LINK_OPTIONS}>{t('registration.newsletterDescription_QS', { url: PRIVACY_POLICY })}</Markdown>
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {t('registration.newsletterDescription')} <br />
                                                        <small className="mb-4"><Markdown options={MARKDOWN_EXTERNAL_LINK_OPTIONS}>{t('registration.newsletterPrivacy', { url: PRIVACY_POLICY })}</Markdown></small>
                                                    </span>
                                                )
                                            } 
                                        />
                                    </Form.Group>
                                    {/* AGB */}
                                    <Form.Group className="mb-4 event__registration-formgroup" controlId="formBasicCheckboxAcceptPrivacy">
                                        <Form.Check 
                                            type="checkbox" 
                                            checked={registration.authorize_data_transfer} 
                                            onChange={(e) => handleRegistrationChange('authorize_data_transfer', e.target.checked)} 
                                            required 
                                            label={
                                                APP_VARIANT === 'QS' ? (
                                                    <span className="d-flex align-items-start">
                                                        <span className="me-1">*</span>
                                                        <Markdown options={MARKDOWN_EXTERNAL_LINK_OPTIONS}>{t('registration.acceptTerms_QS', { 
                                                            url1: i18n.language.startsWith('en') ? QS_URL1_EN : QS_URL1_DE, 
                                                            url2: i18n.language.startsWith('en') ? QS_URL2_EN : QS_URL2_DE, 
                                                            url3: i18n.language.startsWith('en') ? QS_URL3_EN : QS_URL3_DE, 
                                                            url4: i18n.language.startsWith('en') ? QS_URL4_EN : QS_URL4_DE 
                                                        })}</Markdown>
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {t('registration.acceptTerms')} *
                                                        <br />
                                                        <small>{t('registration.termsDetails')} <a href={renderAGBUrl()} target="_blank">{t('registration.termsOfService')}</a>.</small>
                                                    </span>
                                                )
                                            } 
                                        />
                                    </Form.Group>
                                    <Button variant={APP_VARIANT === 'QS' ? 'secondary' : 'primary'} type="submit" className={`${APP_VARIANT === 'QS' ? 'rounded-0' : 'rounded-pill'} event__registration-submitbutton`}>
                                        {t('registration.confirmInput')}
                                    </Button>
                                </Form>
                            </Col>
                        </Row>
                    </Container>
                </>
            }
        </>
    );
}

export default EventRegistrationForm;